import {useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {useAtom, useSetAtom} from "jotai";
import _ from "lodash";
import dayjs from "dayjs";
import {
    MRT_ColumnDef,
    MaterialReactTable,
    MRT_VisibilityState,
    MRT_Updater,
    MRT_ColumnSizingState,
    useMaterialReactTable,
    MRT_ShowHideColumnsButton,
} from "material-react-table";
import {Alert, Stack, Box, Typography, Button, IconButton} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Company} from "../../types/domain/company";
import {CompanyEditDialog} from "../dialogs/CompanyEditDialog";
import {EditIcon} from "../icons/EditIcon";
import {getFullAddress} from "../../utils/addressUtils";
import {
    adminCompaniesTableColumnsVisibilityAtom,
    adminCompaniesTableColumnsSizeAtom,
} from "../../store/adminCompaniesTableStore";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {ALL, BOOLEAN_OPTIONS, enumItemToTableFilterSelectOption} from "../../utils/enumItemsUtils";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {TableFilterSelectOption} from "../../types/table";
import {authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {formatShortDate} from "../../utils/dateUtils";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {routerFarmIdAtom, routerFieldIdAtom} from "../../store/routerStore";
import {useQueryClient} from "@tanstack/react-query";
import {useCompanies} from "../../hooks/companiesHooks";

export const AdminCompaniesView: React.FC = () => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const {dateFormat} = useUserPreferences();
    const companies = useCompanies();
    const setFarmId = useSetAtom(routerFarmIdAtom);
    const setFieldId = useSetAtom(routerFieldIdAtom);
    const navigate = useNavigate();
    const [activeCompany, setActiveCompany] = useState<Company | undefined>(undefined);
    const [companyCreateDialogOpened, setCompanyCreateDialogOpened] = useState<boolean>(false);
    const [companyEditDialogOpened, setCompanyEditDialogOpened] = useState<boolean>(false);
    const [createdCompaniesAlerts, setCreatedCompaniesAlerts] = useState<Company[]>([]);
    const [adminCompaniesTableColumnsVisibility, setAdminCompaniesTableColumnsVisibility] = useAtom(
        adminCompaniesTableColumnsVisibilityAtom,
    );
    const [adminCompaniesTableColumnsSize, setAdminCompaniesTableColumnsSize] = useAtom(
        adminCompaniesTableColumnsSizeAtom,
    );

    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<Company>(locale);
    const {items: isResellerOptions} = useLocalizedEnumItems(IS_RESELLER_OPTIONS);
    const {items: apiEnabledOptions} = useLocalizedEnumItems(API_ENABLED_OPTIONS);
    const columns = useMemo(() => {
        const handleCompanyClick = (companyId: string) => {
            setFarmId(undefined);
            setFieldId(undefined);
            navigate({
                pathname: `/organization/${companyId}`,
                search: location.search,
            });
        };
        return getTableColumns(
            t,
            isResellerOptions.map(enumItemToTableFilterSelectOption),
            apiEnabledOptions.map(enumItemToTableFilterSelectOption),
            dateFormat,
            handleCompanyClick,
        );
    }, [t, isResellerOptions, apiEnabledOptions, dateFormat, navigate, setFarmId, setFieldId]);

    const onCompanyCreateDialogClose = (isSuccessful: boolean, companyResponse?: Company) => {
        setCompanyCreateDialogOpened(false);
        if (isSuccessful) {
            if (companyResponse) {
                setCreatedCompaniesAlerts([...createdCompaniesAlerts, companyResponse]);
            }
            void queryClient.invalidateQueries({queryKey: ["company"]});
            void queryClient.invalidateQueries({queryKey: ["parentCompany"]});
        }
    };

    const onCompanyEditDialogClose = (isSuccessful: boolean) => {
        setCompanyEditDialogOpened(false);
        setActiveCompany(undefined);
        if (isSuccessful) {
            void queryClient.invalidateQueries({queryKey: ["company"]});
            void queryClient.invalidateQueries({queryKey: ["parentCompany"]});
        }
    };

    const onColumnVisibilityChange = (v: MRT_VisibilityState | MRT_Updater<MRT_VisibilityState>) => {
        setAdminCompaniesTableColumnsVisibility(v);
    };

    const onColumnSizingChange = (v: MRT_ColumnSizingState | MRT_Updater<MRT_ColumnSizingState>) => {
        setAdminCompaniesTableColumnsSize(v as Record<string, number>);
    };

    const table = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 12rem)",
                minHeight: "calc(100vh - 12rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnPinning: {
                right: ["mrt-row-actions"],
            },
            pagination: {
                pageIndex: 0,
                pageSize: 20,
            },
            columnFilters: [
                {
                    id: "isReseller",
                    value: "ALL",
                },
                {
                    id: "isApiEnabled",
                    value: "ALL",
                },
            ],
        },
        state: {
            isLoading: companies.isLoading,
            columnVisibility: adminCompaniesTableColumnsVisibility,
            ...(adminCompaniesTableColumnsSize && {columnSizing: adminCompaniesTableColumnsSize}),
        },
        columns,
        data: companies.isSuccess ? companies.data : [],
        muiTableBodyCellProps: {
            sx: {
                whiteSpace: "nowrap",
                paddingTop: "0.5rem",
                paddingBottom: "0.5rem",
            },
        },
        enableColumnResizing: true,
        onColumnVisibilityChange: onColumnVisibilityChange,
        onColumnSizingChange: onColumnSizingChange,
        displayColumnDefOptions: {
            "mrt-row-actions": {
                enableResizing: false,
                size: 30,
                header: "",
            },
        },
        muiTableHeadRowProps: () => ({sx: {backgroundColor: "#F3F5FA"}}),
        renderRowActions: ({row}) => (
            <Box sx={{display: "flex", gap: "1rem"}}>
                <IconButton
                    color="primary"
                    onClick={() => {
                        setActiveCompany(row.original);
                        setCompanyEditDialogOpened(true);
                    }}
                >
                    <EditIcon
                        sx={{
                            stroke: "#303A8C",
                            width: "1.125rem",
                            height: "1.125rem",
                        }}
                    />
                </IconButton>
            </Box>
        ),
    });

    return (
        <Stack paddingX="1.5rem" paddingBottom="0.5rem">
            <Stack direction="row" height="4rem" alignItems="center" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                    {t("admin.companies_view.title")}
                </Typography>
                <Stack direction="row">
                    <Button startIcon={<AddIcon />} onClick={() => setCompanyCreateDialogOpened(true)}>
                        {t("admin.companies_view.create_button")}
                    </Button>
                    <MRT_ShowHideColumnsButton table={table} />
                </Stack>
            </Stack>
            {createdCompaniesAlerts.map((company) => (
                <Alert
                    key={company.id}
                    severity="success"
                    variant="filled"
                    color="info"
                    sx={{mt: "1.25rem"}}
                    onClose={() => {
                        void queryClient.invalidateQueries({queryKey: ["company"]});
                        void queryClient.invalidateQueries({queryKey: ["parentCompany"]});
                        setCreatedCompaniesAlerts(_.filter(createdCompaniesAlerts, (x) => x.id !== company.id));
                    }}
                >
                    {t("admin.companies_view.company_created", {name: company.properties.name})}
                </Alert>
            ))}
            <MaterialReactTable table={table} />
            {companyCreateDialogOpened && (
                <CompanyEditDialog
                    mode="create"
                    open={companyCreateDialogOpened}
                    onClose={onCompanyCreateDialogClose}
                />
            )}
            {companyEditDialogOpened && activeCompany !== undefined && (
                <CompanyEditDialog
                    mode="edit"
                    company={activeCompany}
                    open={companyEditDialogOpened}
                    onClose={onCompanyEditDialogClose}
                />
            )}
        </Stack>
    );
};

const IS_RESELLER_OPTIONS = [ALL, ...BOOLEAN_OPTIONS];
const API_ENABLED_OPTIONS = [ALL, ...BOOLEAN_OPTIONS];

const getTableColumns = (
    t: TFunction<string, undefined>,
    isResellerOptions: TableFilterSelectOption[],
    apiEnabledOptions: TableFilterSelectOption[],
    dateFormat: string,
    handleCompanyClick: (companyId: string) => void,
): MRT_ColumnDef<Company>[] => [
    {
        id: "name",
        header: t("admin.companies_view.columns.name"),
        accessorKey: "properties.name",
        filterFn: "contains",
        Cell: ({renderedCellValue, row}) => (
            <Box
                component="span"
                onClick={() => handleCompanyClick(row.original.id)}
                sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    outline: "none",
                    "&:focus": {
                        outline: "2px solid #FFD600", // MRT's yellow
                        outlineOffset: "2px",
                    },
                }}
            >
                {renderedCellValue}
            </Box>
        ),
        minSize: 100,
    },
    {
        id: "id",
        header: t("admin.companies_view.columns.id"),
        accessorKey: "id",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "address",
        header: t("admin.companies_view.columns.address"),
        accessorFn: (row) => (row.properties.address ? getFullAddress(row.properties.address) : ""),
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "phone",
        header: t("admin.companies_view.columns.phone"),
        accessorKey: "properties.phone",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "vat",
        header: t("admin.companies_view.columns.vat"),
        accessorKey: "properties.vat",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "invoiceEmail",
        header: t("admin.companies_view.columns.invoice"),
        accessorKey: "properties.invoiceEmail",
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "isReseller",
        header: t("admin.companies_view.columns.reseller"),
        accessorKey: "isReseller",
        minSize: 80,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: isResellerOptions,
        filterFn: (row, _, filter) => filter === "ALL" || row.original.isReseller === (filter === "YES"),
        Cell: ({row}) => (row.original.isReseller ? t("enumerations.boolean.yes") : t("enumerations.boolean.no")),
    },
    {
        id: "isApiEnabled",
        header: t("admin.companies_view.columns.api_enabled"),
        accessorKey: "isApiEnabled",
        minSize: 80,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: apiEnabledOptions,
        filterFn: (row, _, filter) => filter === "ALL" || row.original.isApiEnabled === (filter === "YES"),
        Cell: ({row}) => (row.original.isApiEnabled ? t("enumerations.boolean.yes") : t("enumerations.boolean.no")),
    },
    {
        id: "createdAt",
        header: t("admin.companies_view.columns.created_at"),
        accessorFn: (data: Company) => formatShortDate(dayjs(data.createdAt), dateFormat),
        sortingFn: (a, b) => {
            return new Date(a.original.createdAt).valueOf() - new Date(b.original.createdAt).valueOf();
        },
        filterFn: "contains",
        minSize: 100,
    },
    {
        id: "updatedAt",
        header: t("admin.companies_view.columns.updated_at"),
        accessorFn: (data: Company) => formatShortDate(dayjs(data.updatedAt), dateFormat),
        sortingFn: (a, b) => {
            return new Date(a.original.updatedAt).valueOf() - new Date(b.original.updatedAt).valueOf();
        },
        minSize: 100,
    },
];
